import { useMemo, useState } from "react";
import { AppLayout } from "../../components/layout/Header";
import {
  getLifecycle,
  listBrokerAgents,
  lockForCompliance,
  requestReactivate,
  statusLabel,
  suspendAccount,
  unlockCompliance,
} from "../../js/api/accountLifecycle";
import { useAuth } from "../../js/hooks/useAuth";

export default function BrokerAgentControls() {
  const { session } = useAuth();
  const [tick, setTick] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const agents = useMemo(() => listBrokerAgents(session.userId), [session.userId, tick]);
  const selected = agents.find((row) => row.id === selectedId) || null;
  const life = selected ? getLifecycle(selected.id) : null;

  function actor() {
    return { userId: session.userId, role: "broker" };
  }

  function run(action) {
    if (!selected) return;
    try {
      action();
      setTick((value) => value + 1);
      setNote("Office record updated. A notice is kept on this device.");
    } catch (error) {
      setNote(error.message);
    }
  }

  return (
    <AppLayout variant="broker">
      <div className="page-header">
        <div>
          <h1>Agent Controls</h1>
          <p className="page-lead">
            Quiet oversight for this office. Suspend, restore, or lock a desk for a compliance review.
            Buyer and seller files stay on their own carpets.
          </p>
        </div>
      </div>
      {note ? <p className="confirm-banner">{note}</p> : null}
      <div className="two-col">
        <article className="card">
          <h2>Agents</h2>
          <ul className="account-sessions">
            {agents.length ? (
              agents.map((agent) => {
                const row = getLifecycle(agent.id);
                return (
                  <li key={agent.id}>
                    <button type="button" className="link-btn" onClick={() => setSelectedId(agent.id)}>
                      {agent.name || agent.email}
                    </button>
                    <span className="muted"> · {statusLabel(row.status)}</span>
                  </li>
                );
              })
            ) : (
              <li className="muted">No agents are linked to this office yet.</li>
            )}
          </ul>
        </article>
        <article className="card stack">
          {selected && life ? (
            <>
              <h2>{selected.name}</h2>
              <p className="muted">{selected.email}</p>
              <p>Status: {statusLabel(life.status)}</p>
              {life.suspensionUntil ? (
                <p className="muted">
                  Suspension window through {new Date(life.suspensionUntil).toLocaleDateString()}
                  {life.suspensionKind ? ` · ${life.suspensionKind}` : ""}
                </p>
              ) : null}
              <p>Permissions: desk {life.status === "active" ? "open" : "paused"}</p>
              <p>Client portals: {life.allowClientPortals ? "may stay open" : "closed"}</p>
              <label className="field">
                <span className="field-label">Office note</span>
                <textarea className="field-input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
              </label>
              <div className="btn-row">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => run(() => suspendAccount(selected.id, actor(), { reason, allowClientPortals: false }))}
                >
                  Suspend
                </button>
                <button type="button" className="ghost-btn" onClick={() => run(() => requestReactivate(selected.id, actor()))}>
                  Reactivate
                </button>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => run(() => lockForCompliance(selected.id, actor(), { reason }))}
                >
                  Compliance lock
                </button>
                <button type="button" className="ghost-btn" onClick={() => run(() => unlockCompliance(selected.id, actor()))}>
                  Release lock
                </button>
              </div>
              <h3>History</h3>
              <ul className="account-sessions">
                {(life.history || []).length ? (
                  life.history.map((row) => (
                    <li key={`${row.at}-${row.action}`}>
                      {new Date(row.at).toLocaleString()} · {row.action}
                      {row.metadata?.reason ? ` · ${row.metadata.reason}` : ""}
                    </li>
                  ))
                ) : (
                  <li className="muted">No events yet.</li>
                )}
              </ul>
            </>
          ) : (
            <p className="muted">Select an agent to review status, permissions, and activity.</p>
          )}
        </article>
      </div>
    </AppLayout>
  );
}
